'use client';

import React, { useEffect, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import axios from 'axios';

// hooks
import usePrevState from 'hooks/usePrevState';

// components
import { PrimartyBtn as Find } from 'components/Buttons';
import GoogleMap from './GoogleMap';
import RecordStoreList from './RecordStoreList';
import LocationInput from './LocationInput';

const RecordFinderContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  height: 600px;
  border: 1px solid var(--darkGrey);
  @media only screen and (min-width: 768px) {
    flex-direction: row;
  }
`;

const Container = styled.div`
  position: relative;
`;

const ContainerFlexRow = styled.div`
  display: flex;
  padding: 15px;
  margin: 0 0 10px 0;
  justify-content: space-between;
  background: var(--darkGrey);
  @media only screen and (min-width: 768px) {
  }
`;

const NoResults = styled.div`
  position: absolute;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 500px;
  z-index: 100000;
  justify-content: center;
  align-items: center;
`;

const NoResultsHeader = styled.h3`
  font-family: ${(props) => props.theme.fonts.sansSerif};
  font-size: 1.8rem;
  color: var(--darkGrey);
`;

const defaultLatLon = {
  lat: 41.881832,
  lng: -87.623177
};

const RecordStoreFinder = () => {
  const [results, setResults] = useState();
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [userGeolocation, setUserGeoLocation] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [clearZipValue, setClearZipValue] = useState(false);

  const zipRef = useRef();

  const prevResultState = usePrevState(zipRef.current);

  const getRecordstoreResults = useCallback(async (latLng) => {
    if (latLng) {
      const { lat, lng } = latLng;
      const radius = 16500;
      const res = await axios.get(
        `/api/googleMaps?name=record store&location=${lat},${lng}&radius=${radius}`
      );
      setIsLoading(false);
      setResults([...res.data.result.results]);
    }
  }, []);

  useEffect(() => {
    getRecordstoreResults(defaultLatLon);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // browser geolocation
  const getUserLocation = () => {
    if (navigator.geolocation) {
      setIsLoading(true);
      setClearZipValue(true);
      navigator.geolocation.getCurrentPosition((position) => {
        const { latitude, longitude } = position.coords;
        setUserGeoLocation({ lat: latitude, lng: longitude });
        setIsLoading(false);
        getRecordstoreResults({ lat: latitude, lng: longitude });
        setClearZipValue(false);
      });
    }
  };

  // zipcode input
  const getLatLngFromZip = async (value) => {
    zipRef.current = value;
    if (prevResultState !== value) {
      const { data } = await axios.get(`/api/googleMaps/geoCode?zipcode=${value}`);
      if (data.result.results.length > 0) {
        const zipLatLng = data.result.results[0].geometry.location;
        setUserGeoLocation(zipLatLng);
        setIsLoading(true);
        return getRecordstoreResults(zipLatLng);
      }
      return setResults([]);
    }
    return null;
  };

  return (
    <Container>
      <ContainerFlexRow>
        <Find
          label="Find me"
          backgroundColor="#FF6969"
          textColor="#fff"
          clickEvent={() => !isLoading && getUserLocation()}
          active={!isLoading}
        />
        <LocationInput
          clearValue={clearZipValue}
          userInput={(value) => getLatLngFromZip(value)}
          isLoading={!isLoading}
        />
      </ContainerFlexRow>

      <RecordFinderContainer>
        {results?.length === 0 && (
          <NoResults>
            <NoResultsHeader>No Results</NoResultsHeader>
          </NoResults>
        )}
        <GoogleMap
          defaultLatLon={defaultLatLon}
          userGeolocation={userGeolocation}
          results={results}
          markerCb={(marker) => setSelectedMarker(marker)}
          isLoading={isLoading}
        />

        <RecordStoreList
          results={results}
          selectedMarker={selectedMarker}
          selectedListItem={(index) => {
            setUserGeoLocation(results[index].geometry.location);
          }}
        />
      </RecordFinderContainer>
    </Container>
  );
};

RecordStoreFinder.propTypes = {
  defaultLatLon: PropTypes.shape({ lat: PropTypes.number, lng: PropTypes.number })
};

export default RecordStoreFinder;
